import { useInterval } from 'ahooks'
import { useCallback, useEffect, useRef, useState } from 'react'

import { API } from '@/api'
import { GetBriefMonitorDataRes } from '@/api/type-response'
import { LoginScope } from '@/consts/team'
import { Instance } from '@/models/instance'
import { useTeamStore } from '@/stores/team'
import { getToken as getPlatformToken } from '@/utils/auth'

interface UseBriefInstanceMonitorDataParams {
  instance: Instance
  pollingInterval?: number
  enabled?: boolean
}

interface UseBriefInstanceMonitorDataReturn {
  info: GetBriefMonitorDataRes | undefined
  loading: boolean
  grantPending: boolean
  isTeamContext: boolean
  refetch: () => Promise<void>
}

function normalizeBearer(token?: string | null): string | null {
  if (!token) return null
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`
}

function getCurrentUserBearer(): string | null {
  const teamRaw = useTeamStore.getState().teamTokenData?.token
  const teamBearer = normalizeBearer(teamRaw)
  if (teamBearer) return teamBearer

  const platformRaw = getPlatformToken()
  const platformBearer = normalizeBearer(platformRaw)
  if (platformBearer) return platformBearer

  return null
}

/**
 * Hook to fetch brief instance monitor data
 * Handles both Platform and Team API calls automatically based on context
 *
 * @param instance - The instance to monitor
 * @param pollingInterval - Polling interval in milliseconds (default: 30000)
 * @param enabled - Whether to enable data fetching (default: true)
 * @returns Object containing monitor info, loading states, and refetch function
 */
export function useBriefInstanceMonitorData({
  instance,
  pollingInterval = 30000,
  enabled = true,
}: UseBriefInstanceMonitorDataParams): UseBriefInstanceMonitorDataReturn {
  const [info, setInfo] = useState<GetBriefMonitorDataRes | undefined>()
  const [loading, setLoading] = useState(false)
  const [grantPending, setGrantPending] = useState(false)
  const [monitorBearer, setMonitorBearer] = useState<string | null>(null)
  const fetchingRef = useRef(false)

  const scopeInfo = useTeamStore((state) => state.scopeInfo)
  const isTeamContext = scopeInfo?.scope === LoginScope.Team

  const instanceIdNum = Number(
    (instance as any).id ?? (instance as any).realID ?? instance.displayID,
  )

  // Grant token for team context
  useEffect(() => {
    if (!isTeamContext) {
      // Platform context, no need to grant
      setMonitorBearer(null)
      setGrantPending(false)
      return
    }

    let mounted = true
    ;(async () => {
      setGrantPending(true)
      const fallbackBearer = getCurrentUserBearer()

      if (!Number.isFinite(instanceIdNum)) {
        if (mounted) {
          setMonitorBearer(fallbackBearer)
          setGrantPending(false)
        }
        return
      }

      try {
        const res = await API.grantNodeMetricsAccess({
          privilege_type: 65,
          source_id: instanceIdNum,
        })
        if (mounted) {
          setMonitorBearer(res?.code === 0 && res?.token ? res.token : fallbackBearer)
          setGrantPending(false)
        }
      } catch {
        if (mounted) {
          setMonitorBearer(fallbackBearer)
          setGrantPending(false)
        }
      }
    })()

    return () => {
      mounted = false
    }
  }, [isTeamContext, instanceIdNum])

  const fetchBriefMonitorData = useCallback(async () => {
    if (fetchingRef.current || !instance?.displayID || !enabled) {
      return
    }

    // In team context, wait for token grant
    if (isTeamContext && !monitorBearer) {
      return
    }

    fetchingRef.current = true
    setLoading(true)

    try {
      let res: GetBriefMonitorDataRes & { code: number; msg: string }

      if (isTeamContext && monitorBearer) {
        // Team context with token
        res = await API._getBriefMonitorDataWithToken(
          { request_id: instance.displayID },
          monitorBearer,
        )
      } else {
        // Platform context
        res = await API.getBriefMonitorData({ request_id: instance.displayID })
      }

      if (res.code === 0) {
        setInfo(res)
      }
    } catch (error) {
      console.error('Failed to fetch brief monitor data:', error)
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [instance?.displayID, isTeamContext, monitorBearer, enabled])

  // Auto-polling
  useInterval(
    () => {
      if (enabled && !grantPending) {
        fetchBriefMonitorData()
      }
    },
    pollingInterval,
    { immediate: true },
  )

  // Initial fetch after token is ready
  useEffect(() => {
    if (!grantPending && enabled) {
      fetchBriefMonitorData()
    }
  }, [grantPending, enabled, fetchBriefMonitorData])

  return {
    info,
    loading,
    grantPending,
    isTeamContext,
    refetch: fetchBriefMonitorData,
  }
}
